import { Tenant, TenantTheme, ColorScale, UserPoolConfig } from '@/types'

// Bootstrap response interface (from our tenant API)
interface BootstrapResponse {
  tenantId: string
  displayName: string
  amplifyConfig: {
    Auth: {
      Cognito: {
        userPoolId: string
        userPoolClientId: string
        region: string
        identityPoolId?: string
        loginWith?: {
          username: boolean
          email: boolean
          phone_number?: boolean
        }
      }
    }
  } | null
  theme: {
    logoUrl: string | null
    primaryColor: string
    secondaryColor: string
    customCss: string | null
  }
  userPoolConfig: UserPoolConfig | null
}

export class TenantService {
  private cache = new Map<string, Tenant>()
  private themeCache = new Map<string, TenantTheme>()
  private cacheTTL = 5 * 60 * 1000 // 5 minutes
  private cacheTimestamps = new Map<string, number>()
  private readonly LOCALSTORAGE_KEY = 'tenant-override'

  // Bootstrap endpoint configuration
  // Always returns the default endpoint - fallback logic is handled in getTenantByHostname
  private getBootstrapEndpoint(): string {
    return '/tenant/bootstrap'
  }

  // Get fallback endpoint from env var (used when default endpoint fails)
  private getFallbackBootstrapEndpoint(): string {
    return import.meta.env.VITE_TENANT_BOOTSTRAP_API_ENDPOINT_URL
  }

  /**
   * Get the effective tenant domain for API calls.
   *
   * Resolution order:
   *   1. If hostname is `monutrack.dev` / `monutrack.app` OR any subdomain
   *      of them, use the hostname directly — every tenant lives at
   *      `<sub>.monutrack.{dev,app}`, so the subdomain IS the tenant identity.
   *   2. Otherwise (localhost, Vercel preview URLs, custom hostnames),
   *      fall back to `VITE_TENANT_DOMAIN`.
   *   3. If no env var is set, default to `monutrack.app`.
   *
   * Only canonical-domain hosts are trusted from the URL; arbitrary
   * hostnames defer to the build-time env var.
   */
  getEffectiveTenantDomain(hostname: string): string {
    if (hostname === 'monutrack.dev' || hostname.endsWith('.monutrack.dev')) return hostname
    if (hostname === 'monutrack.app' || hostname.endsWith('.monutrack.app')) return hostname

    const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
    if (envTenantDomain) {
      return envTenantDomain
    }

    return 'monutrack.app'
  }


  /**
   * Build headers for the bootstrap request.
   * Uses getEffectiveTenantDomain to determine the tenant domain.
   */
  private getBootstrapHeaders(hostname: string): HeadersInit {
    const tenantDomain = this.getEffectiveTenantDomain(hostname)

    return {
      Accept: 'application/json',
      'X-Tenant-Domain': tenantDomain,
    }
  }

  /**
   * Map bootstrap response to Tenant interface
   */
  private mapBootstrapToTenant(
    bootstrap: BootstrapResponse,
    hostname: string
  ): Tenant {
    const subdomain = hostname.split('.')[0] || hostname

    const tenant = {
      tenantId: bootstrap.tenantId,
      displayName: bootstrap.displayName, // Add displayName for configureAmplifyWithTenant
      name: bootstrap.displayName,
      status: 'active' as const,
      features: {
        darkMode: true,
        smsVerification: true,
        biometricAuth: false,
        bulkSend: true,
        apiAccess: true,
        customBranding: true,
        themeInheritance: true,
      },
      theme: bootstrap.theme, // Simple theme for bootstrap configuration
      detailedTheme: this.mapBootstrapTheme(bootstrap.theme), // Detailed theme for frontend
      config: {
        domains: [hostname],
        features: {
          enabledFeatures: ['darkMode', 'smsVerification', 'bulkSend', 'apiAccess', 'customBranding'],
          disabledFeatures: ['biometricAuth']
        },
        branding: {
          logo: bootstrap.theme.logoUrl || '/logo.svg',
          primaryColor: bootstrap.theme.primaryColor,
          secondaryColor: bootstrap.theme.secondaryColor,
          customCss: null
        },
        responseHeaders: {}
      },
      limits: {
        monthlyDocuments: 100,
        storage: 1000000000, // 1GB
        users: 10,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      amplifyConfig: bootstrap.amplifyConfig, // CRITICAL: Preserve amplifyConfig for configureAmplifyWithTenant
      userPoolConfig: bootstrap.userPoolConfig || undefined // Store user pool configuration for dynamic forms
    }

    return tenant as unknown as Tenant
  }

  /**
   * Map bootstrap theme to TenantTheme interface
   */
  private mapBootstrapTheme(
    bootstrapTheme: BootstrapResponse['theme']
  ): TenantTheme {
    const defaultTheme = this.getDefaultTheme()

    // Parse colors from bootstrap theme
    const primaryColor = bootstrapTheme.primaryColor || '#1E40AF'
    const secondaryColor = bootstrapTheme.secondaryColor || '#3B82F6'

    return {
      ...defaultTheme,
      brand: {
        ...(defaultTheme as any).brand,
        logo: {
          primary: bootstrapTheme.logoUrl || '/logo.svg',
          favicon: '/favicon.ico',
        },
        colors: {
          ...(defaultTheme as any).brand.colors,
          primary: this.generateColorScale(primaryColor),
          secondary: this.generateColorScale(secondaryColor),
        },
      },
      customCss: bootstrapTheme.customCss || undefined,
    } as TenantTheme
  }

  /**
   * Generate color scale from single color
   */
  private generateColorScale(baseColor: string): ColorScale {
    // For now, return a basic scale - in the future this could be more sophisticated
    return {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: baseColor, // Base color
      600: baseColor,
      700: baseColor,
      800: baseColor,
      900: baseColor,
      950: baseColor,
    }
  }

  async getTenantBySubdomain(subdomain: string): Promise<Tenant> {
    // Use hostname-based method for better compatibility
    const hostname = `${subdomain}.example.com`
    return this.getTenantByHostname(hostname)
  }

  async getTenantByHostname(hostname: string): Promise<Tenant> {
    const effectiveDomain = this.getEffectiveTenantDomain(hostname)
    console.log('🔗 Fetching tenant config for:', effectiveDomain)

    // Prepare headers with tenant domain
    const headers = this.getBootstrapHeaders(hostname)

    // Try primary endpoint first
    const bootstrapEndpoint = this.getBootstrapEndpoint()
    let response: Response | null = null
    let lastError: Error | null = null

    try {
      response = await fetch(bootstrapEndpoint, { headers, method: 'GET' })
      if (response.ok) {
        const bootstrapData: BootstrapResponse = await response.json()
        const tenant = this.mapBootstrapToTenant(bootstrapData, hostname)
        this.cacheTenant(hostname, tenant)
        return tenant
      }
    } catch (error) {
      console.log('⚠️ Primary endpoint failed:', error)
      lastError = error instanceof Error ? error : new Error(String(error))
    }

    // Try fallback endpoint if configured
    const fallbackEndpoint = this.getFallbackBootstrapEndpoint()
    if (fallbackEndpoint) {
      console.log(`⚠️ Primary failed${response ? ` (${response.status})` : ''}, trying fallback:`, fallbackEndpoint)
      try {
        response = await fetch(fallbackEndpoint, { headers, method: 'GET' })
        if (response.ok) {
          const bootstrapData: BootstrapResponse = await response.json()
          const tenant = this.mapBootstrapToTenant(bootstrapData, hostname)
          this.cacheTenant(hostname, tenant)
          return tenant
        }
      } catch (error) {
        console.error('❌ Fallback endpoint failed:', error)
        lastError = error instanceof Error ? error : new Error(String(error))
      }
    }

    // Both failed
    if (response) {
      if (response.status === 404) {
        throw new Error(`Tenant not found for hostname: ${hostname}`)
      }
      throw new Error(`Failed to fetch tenant bootstrap: ${response.statusText}`)
    }

    throw new Error(lastError?.message || 'Failed to fetch tenant configuration')
  }

  /**
   * Save tenant data to localStorage (for development overrides)
   */
  saveTenantToLocalStorage(tenant: Tenant): void {
    try {
      const tenantData = {
        ...tenant,
        // Ensure dates are strings for JSON serialization
        createdAt: tenant.createdAt,
        updatedAt: new Date().toISOString(),
      }

      localStorage.setItem(this.LOCALSTORAGE_KEY, JSON.stringify(tenantData))

      // Clear cache to force reload
      this.clearCache()
    } catch (error) {
      console.error('Failed to save tenant to localStorage:', error)
    }
  }

  /**
   * Get tenant data from localStorage
   */
  getLocalStorageTenant(): Tenant | null {
    try {
      const stored = localStorage.getItem(this.LOCALSTORAGE_KEY)
      if (!stored) return null

      const tenantData = JSON.parse(stored)

      // Convert date strings back to Date objects
      return {
        ...tenantData,
        createdAt: new Date(tenantData.createdAt),
        updatedAt: new Date(tenantData.updatedAt),
      }
    } catch (error) {
      console.error('Failed to load tenant from localStorage:', error)
      return null
    }
  }

  /**
   * Remove tenant override from localStorage
   */
  clearLocalStorageTenant(): void {
    try {
      localStorage.removeItem(this.LOCALSTORAGE_KEY)

      // Clear cache to force reload
      this.clearCache()
    } catch (error) {
      console.error('Failed to clear tenant from localStorage:', error)
    }
  }

  /**
   * Update specific tenant properties (useful for theme updates)
   */
  updateLocalStorageTenant(updates: Partial<Tenant>): Tenant | null {
    const existingTenant = this.getLocalStorageTenant()
    if (!existingTenant) {
      throw new Error(
        'No tenant configuration available in localStorage to update'
      )
    }

    const updatedTenant: Tenant = {
      ...existingTenant,
      ...updates,
      updatedAt: new Date().toISOString(),
    }

    this.saveTenantToLocalStorage(updatedTenant)
    return updatedTenant
  }

  /**
   * Check if localStorage override is active
   */
  hasLocalStorageOverride(): boolean {
    return !!localStorage.getItem(this.LOCALSTORAGE_KEY)
  }

  private getCachedTenant(subdomain: string): Tenant | null {
    const cached = this.cache.get(subdomain)
    const timestamp = this.cacheTimestamps.get(subdomain)

    if (cached && timestamp && Date.now() - timestamp < this.cacheTTL) {
      return cached
    }

    // Clean up expired cache
    this.cache.delete(subdomain)
    this.cacheTimestamps.delete(subdomain)
    return null
  }

  private cacheTenant(subdomain: string, tenant: Tenant): void {
    this.cache.set(subdomain, tenant)
    this.cacheTimestamps.set(subdomain, Date.now())
  }

  private getDefaultTheme(): TenantTheme {
    const trustBlue: ColorScale = {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#1E40AF', // Trust Blue
      600: '#1d4ed8',
      700: '#1e3a8a',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#1e3a8a',
    }

    const seamlessBlue: ColorScale = {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3B82F6', // Seamless Blue
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#1e3a8a',
    }

    const successGreen: ColorScale = {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10B981', // Success Green
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#064e3b',
    }

    return {
      primaryColor: trustBlue[500],
      secondaryColor: seamlessBlue[500],
      logo: '/logo.svg',
      customCss: undefined,
      brand: {
        logo: {
          primary: '/logo.svg',
          favicon: '/favicon.ico',
        },
        colors: {
          primary: trustBlue,
          secondary: seamlessBlue,
          accent: successGreen,
          semantic: {
            success: '#10B981',
            warning: '#F59E0B',
            error: '#EF4444',
            info: '#3B82F6',
          },
        },
      },
      typography: {
        fonts: {
          sans: 'Poppins, system-ui, sans-serif',
          serif: 'Playfair Display, serif',
          mono: 'JetBrains Mono, monospace',
        },
        weights: {
          light: 300,
          normal: 400,
          medium: 500,
          semibold: 600,
          bold: 700,
        },
      },
      layout: {
        borderRadius: {
          sm: '0.125rem',
          base: '0.25rem',
          md: '0.375rem',
          lg: '0.5rem',
          xl: '0.75rem',
          full: '9999px',
        },
        shadows: {
          sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        },
      },
      modes: {
        light: {
          background: {
            primary: '#FFFFFF',
            secondary: '#F9FAFB',
            tertiary: '#F3F4F6',
          },
          text: {
            primary: '#111827',
            secondary: '#374151',
            tertiary: '#6B7280',
            inverse: '#FFFFFF',
          },
          border: {
            primary: '#E5E7EB',
            secondary: '#D1D5DB',
            focus: '#3B82F6',
          },
        },
        dark: {
          background: {
            primary: '#111827',
            secondary: '#1F2937',
            tertiary: '#374151',
          },
          text: {
            primary: '#F9FAFB',
            secondary: '#D1D5DB',
            tertiary: '#9CA3AF',
            inverse: '#111827',
          },
          border: {
            primary: '#4B5563',
            secondary: '#6B7280',
            focus: '#3B82F6',
          },
        },
      },
      components: {
        button: {
          borderRadius: '0.375rem',
          fontWeight: '600',
          variants: {
            primary: {
              backgroundColor: '#1E40AF',
              textColor: '#FFFFFF',
              borderColor: '#1E40AF',
              hoverBackgroundColor: '#1D4ED8',
              hoverTextColor: '#FFFFFF',
              hoverBorderColor: '#1D4ED8',
            },
            secondary: {
              backgroundColor: 'transparent',
              textColor: '#1E40AF',
              borderColor: '#1E40AF',
              hoverBackgroundColor: '#1E40AF',
              hoverTextColor: '#FFFFFF',
              hoverBorderColor: '#1E40AF',
            },
            ghost: {
              backgroundColor: 'transparent',
              textColor: '#374151',
              borderColor: 'transparent',
              hoverBackgroundColor: '#F3F4F6',
              hoverTextColor: '#1E40AF',
              hoverBorderColor: 'transparent',
            },
          },
        },
        input: {
          borderRadius: '0.375rem',
          borderWidth: '1px',
          focusRing: '2px solid #3B82F6',
        },
        card: {
          borderRadius: '0.5rem',
          shadow:
            '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #E5E7EB',
        },
      },
    } as TenantTheme
  }

  clearCache(): void {
    this.cache.clear()
    this.themeCache.clear()
    this.cacheTimestamps.clear()
  }

  async refreshTenant(subdomain: string): Promise<Tenant> {
    // Clear cache for this tenant
    this.cache.delete(subdomain)
    this.cacheTimestamps.delete(subdomain)

    // Fetch fresh data
    return this.getTenantBySubdomain(subdomain)
  }
}
